import React from 'react'
import { useTranslation } from 'react-i18next'
import { Toggle } from 'ui/atoms'
import Card from 'ui/atoms/Card/Card'
import NewButton from 'ui/atoms/NewButton/NewButton'
import ModuleHeader from 'ui/components/ModuleHeader/ModuleHeader'
import View from 'ui/components/View/View'
import {
  type ExperimentName,
  type Experiments,
  experimentDefinitions,
  getExperiments,
  resetExperiments,
  setExperiment,
} from 'utils/experiments/experiments'

export default function ExperimentsView(): React.ReactElement {
  const { t } = useTranslation()
  const [experiments, setExperiments] = React.useState<Experiments>(getExperiments)

  const applyChange = (needsReload: boolean): void => {
    if (needsReload) {
      window.location.reload()
      return
    }
    setExperiments(getExperiments())
  }

  const handleToggle = (key: ExperimentName, value: boolean): void => {
    applyChange(setExperiment(key, value))
  }

  const handleReset = (): void => {
    applyChange(resetExperiments())
  }

  return (
    <View
      header={
        <ModuleHeader infoHeader={t('Experiments')}>
          <NewButton variant="outlinedDanger" label={t('Reset All')} onClick={handleReset} />
        </ModuleHeader>
      }
    >
      <Card title={t('🧪 Available Experiments')}>
        <p className="text-gray-500 mb-4">{t('These settings are stored locally and persist across sessions.')}</p>

        <div className="flex flex-col gap-4">
          {experimentDefinitions.map((experiment) => (
            <div
              key={experiment.name}
              className="flex justify-between items-center p-4 border border-gray-300 rounded-lg max-w-2xl"
            >
              <div className="flex-1 mr-4">
                <div className="font-semibold">{experiment.title}</div>
                <code className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded mt-1 inline-block font-mono">
                  {experiment.name}
                </code>
                <p className="text-sm text-gray-600 mt-2">{experiment.description}</p>
              </div>
              <Toggle value={experiments[experiment.name]} onClick={(value) => handleToggle(experiment.name, value)} />
            </div>
          ))}
        </div>

        <p className="text-xs text-gray-400 mt-6">
          {t('Note: You may need to refresh the page for changes to take effect.')}
        </p>
      </Card>
    </View>
  )
}
